import {
  currentStandoutYear,
  useListMutations,
  useListsForContent,
  useStandoutMutations,
  useStandoutStatus,
} from "../../hooks/useCustomQuery";
import { Checkbox, Divider, ListItemText, Menu, MenuItem } from "@mui/material";
import Loading from "../../components/Loading";
import AddIcon from "@mui/icons-material/Add";
import { getContentLabel } from "../../utils/utils";
import { useAuth } from "../../context/authContext";
import { useState } from "react";
import StandoutAddFlow from "../standouts/StandoutAddFlow";
import { StandoutIcon } from "../../components/icons";

const STANDOUT_CONTENT_TYPES = ["movie", "tvshow", "book"];

/** Checkbox that swaps to a spinner while its row's toggle is in flight. */
function RowCheckbox({ checked, loading }) {
  if (loading) {
    return (
      <Checkbox
        size="small"
        icon={<Loading inline size={"18px"} />}
        checkedIcon={<Loading inline size={"18px"} />}
        disableRipple
        checked={true}
      />
    );
  }
  return <Checkbox checked={checked} size="small" />;
}

/**
 * "Add to collection" menu for a content item: create a new collection, toggle
 * this year's standouts, then toggle membership of each of the user's collections.
 */
export default function AddToCollectionDropdown({
  anchorEl,
  contentId,
  contentType,
  onClose,
  onCreateNew,
}) {
  const open = Boolean(anchorEl);
  const [loadingListId, setLoadingListId] = useState(null);
  const [standoutLoading, setStandoutLoading] = useState(false);
  // Season choice / finish-date prompt run in dialogs outside the menu
  const [standoutFlowId, setStandoutFlowId] = useState(null);
  const { user } = useAuth();
  const { data, isLoading } = useListsForContent(contentId, {
    enabled: !!contentId && !!user,
  });
  const { addToList, removeFromList } = useListMutations({
    origin: "user",
    profileKind: null,
    username: user?.username,
  });

  // Standouts use their own hooks rather than useListsForContent/useListMutations:
  // - the year's list may not exist yet (it's created on first add), so there's
  //   no list id to toggle, and the year is the user's local one, sent each call;
  // - besides "contains" the row needs needs_date (prompt for a finish date) and
  //   per-season state for a series;
  // - adding can record a finish date, which the generic /lists/:id routes don't
  //   support — and they refuse standouts lists anyway, so the rules can't be
  //   bypassed;
  // - their mutations invalidate different caches (standouts, open dialogs, and
  //   status/journal/counts when a date is recorded).
  const canBeStandout = STANDOUT_CONTENT_TYPES.includes(contentType);
  const { data: standout, isLoading: standoutStatusLoading } =
    useStandoutStatus(contentId, { enabled: open && !!user && canBeStandout });
  const { addStandout, removeStandout } = useStandoutMutations();

  const handleToggle = async (listId, currentlyContains) => {
    setLoadingListId(listId);
    const promise = currentlyContains
      ? removeFromList({ listId, contentId })
      : addToList({ listId, contentId });
    await promise;
    setLoadingListId(null);
  };

  // A series that is itself a standout only happens for migrated picks
  const standoutChecked = standout?.is_series
    ? standout.contains
    : standout?.state === "contains";

  const handleStandoutClick = async () => {
    if (standoutChecked) {
      setStandoutLoading(true);
      await removeStandout(standout.content_id);
      setStandoutLoading(false);
      return;
    }
    // Choosing a season or a finish date happens in a dialog, so hand off
    if (standout.is_series || standout.needs_date) {
      onClose();
      setStandoutFlowId(contentId);
      return;
    }
    setStandoutLoading(true);
    await addStandout({ contentId: standout.content_id });
    setStandoutLoading(false);
  };

  const standoutSecondary =
    standout?.is_series && !standoutChecked ? "Choose a season" : null;

  return (
    <>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 300,
              maxHeight: 300,
              overflowY: "auto",
              overflowX: "hidden",
              mt: 1.5,
            },
          },
        }}
      >
        {isLoading && (
          <MenuItem disabled>
            <Loading inline />
            <ListItemText
              sx={{ ml: 1 }}
              primary="Loading collections..."
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        )}

        {!isLoading && (
          <MenuItem
            onClick={() => {
              onCreateNew?.();
            }}
          >
            <Checkbox
              size="small"
              icon={<AddIcon fontSize="small" />}
              checkedIcon={<AddIcon fontSize="small" />}
              disableRipple
              checked={true}
            />
            <ListItemText
              primary="Add to new collection"
              primaryTypographyProps={{ variant: "body2", fontWeight: "bold" }}
              sx={{ whiteSpace: "normal" }}
            />
          </MenuItem>
        )}

        {!isLoading && canBeStandout && (
          <MenuItem
            onClick={handleStandoutClick}
            disabled={standoutStatusLoading || standoutLoading}
          >
            <RowCheckbox
              checked={standoutChecked}
              loading={standoutStatusLoading || standoutLoading}
            />
            {/* Rendered bare, not in a ListItemIcon: MenuItem forces those to a
                36px min width (via a more specific selector than sx can beat) */}
            <StandoutIcon
              color="primary"
              fontSize="small"
              sx={{ flexShrink: 0, mr: 0.5 }}
            />
            <ListItemText
              primary={`${standout?.year ?? currentStandoutYear()} standouts`}
              secondary={standoutSecondary}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{ variant: "caption" }}
              sx={{ whiteSpace: "normal" }}
            />
          </MenuItem>
        )}

        {!isLoading && canBeStandout && <Divider />}

        {!isLoading && data?.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary={`You don't have any ${getContentLabel({
                type: contentType,
                lowercase: true,
              })} collections yet`}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        )}

        {!isLoading &&
          data?.map((list) => (
            <MenuItem
              key={list.id}
              onClick={() => handleToggle(list.id, list.contains)}
              disabled={loadingListId === list.id}
            >
              <RowCheckbox
                checked={list.contains}
                loading={loadingListId === list.id}
              />
              <ListItemText
                primary={list.title}
                primaryTypographyProps={{ variant: "body2" }}
                sx={{ whiteSpace: "normal" }}
              />
            </MenuItem>
          ))}
      </Menu>

      <StandoutAddFlow
        contentId={standoutFlowId}
        onClose={() => setStandoutFlowId(null)}
      />
    </>
  );
}
